import merge from 'deepmerge';
import { isNullOrUndefined, UTC, utc, TimeSpan, TimestampRange } from 'rewire-common';
import { observable, freeze, createWatcherFn, WatcherTypeFn, defaultEquals } from 'rewire-core';
import { IGrid, IColumn, IRowData, createColumn, IRow, ICell, IGridOptions, createColumnsToggleMenuItems } from 'rewire-grid';
import {
  Form,
  IFormContext,
  validator,
  field,
  IError,
  ErrorSeverity,
  template,
  SingleParameterBuiltInValidator,
  IToggleMenuItem,
} from 'rewire-ui';
import { ITimecard, getMultipleObject, IReason, INameEntity } from '../DataTypes';
import { DeleteIconRenderer } from '../timecard/CellRenderers';
import { getAdvancedAutoCompleteOptions, AdvancedAutoCompleteActionType } from '../ui/AdvancedAutoComplete';
import { roundTo, reasonCategoryDataMapper } from '../utility/DataConverters';
import { formatTime, formatTimeNoRollover, localTimeZoneUTCNow } from '../utility/DateUtils';
import createDataEntryGrid from '../utility/DataEntryGridModel';
import { getNameSearcher } from '../utility/Searchers';
import addReasonDialogModel from './AddReasonDialogModel';

export interface IGridLevelErrors {
  exist: string;
  overlap: string;
  totalUnits: string;
}

export interface ITimecardGridModelState {
  gridLevelErrors: IGridLevelErrors;
  grid: IGrid;
  earnedDate?: UTC;
}

class TimecardGridModel {
  form: Form;
  completionColumnNames: string[];
  state: ITimecardGridModelState;
  watchTimecardGridValues: WatcherTypeFn;
  watchTimecardGridLength: WatcherTypeFn;

  constructor() {
    this.form = Form.create(
      (form: IFormContext) => ({
        start: form.static().label('Start Time'),
        end: form.static().label('End Time'),
        units: form.static().label('Units'),
        reason: form.static().label('Reason'),
      }),
      { units: '0', start: 'N/A', end: 'N/A', reason: 'N/A' },
    );
    this.state = observable({
      gridLevelErrors: {
        exist: '',
        overlap: '',
        totalUnits: '',
      },
      grid: {} as IGrid,
      earnedDate: undefined,
    });
    this.completionColumnNames = [];
    this.watchTimecardGridValues = createWatcherFn();
    this.watchTimecardGridLength = createWatcherFn();
  }

  reset() {
    this.watchTimecardGridValues();
    this.watchTimecardGridLength();
    this.form.reset();
    this.completionColumnNames.length = 0;
    freeze(() => {
      for (const key of Object.keys(this.state.gridLevelErrors) as (keyof typeof this.state.gridLevelErrors)[]) {
        this.state.gridLevelErrors[key] = '';
      }
    });
    this.grid.set?.([]);
    this.state.earnedDate = undefined;
  }

  load(timecards?: any[], options?: IGridOptions, locked?: boolean, earnedDate?: UTC, viewOnly?: () => boolean) {
    this.reset();
    this.completionColumnNames = ['units', 'reason'];
    this.state.earnedDate = earnedDate || timecards?.[0]?.earnedDate || localTimeZoneUTCNow().startOfDay();
    this.state.grid = this.createGrid(timecards as ITimecard[], options, locked, undefined, viewOnly);
    this.recalculateHeader();
    this.watchTimecardGridValues(
      () => {
        this.grid.rows.map((row: IRow) => {
          row.cells['units'].value;
          row.cells['start'].value;
          row.cells['end'].value;
          row.cells['reason'].value;
        });
      },
      () => setTimeout(() => this.recalculateHeader(), 0),
    );
    this.watchTimecardGridLength(
      () => {
        this.grid.rows.length;
      },
      () => {
        setTimeout(() => {
          this.recalculateHeader();
          for (const row of this.gridDataRows) {
            if (row.cells.hideDeleteIcon.value) {
              row.cells.hideDeleteIcon.value = false;
            }
          }
        }, 0);
      },
    );
  }

  createGridColumns(): IColumn[] {
    const reasonSchema = `
      id
      name
      code
      display
      other(fields: ["category"])
    `;
    const reasonFilter = {
      field: 'category',
      in: ['worked', 'absence', 'break'],
    };
    const overtimeReasonFilter = {
      field: 'category',
      eq: 'overtime',
    };
    const reasonSearcher = getNameSearcher('reasons', () => reasonFilter, reasonSchema, reasonCategoryDataMapper);
    const overtimeReasonSearcher = getNameSearcher('reasons', () => overtimeReasonFilter);
    const cols: IColumn[] = observable([]);

    const unitsDifferenceOfValidator = function (this: IRow, value: number, ...otherValues: string[]): IError | undefined {
      const end = !isNullOrUndefined(otherValues[0]) ? utc(otherValues[0]) : undefined;
      const start = !isNullOrUndefined(otherValues[1]) ? utc(otherValues[1]) : undefined;
      if (end && start && value && Math.abs(value - end.subtract(start, TimeSpan.hours, 4)) > 0.02) {
        return { text: template`must be the difference of ${'all'}`(), severity: ErrorSeverity.Error };
      }
      return undefined;
    };
    const unitsValidator = [
      'required' as SingleParameterBuiltInValidator,
      validator(unitsDifferenceOfValidator, field('end'), field('start')),
      validator('>', 0),
    ];
    const startValidator = validator('requiredWhenOthersAreNotNull', field('end'));
    const endValidator = [validator('requiredWhenOthersAreNotNull', field('start')), validator('>', field('start'))];
    const reasonValidator = 'required' as SingleParameterBuiltInValidator;

    const reasonAction: AdvancedAutoCompleteActionType = { action: this.showAddReasonDialog, label: 'Add New Reason' };

    cols.push(
      createColumn('hideDeleteIcon', '', {
        canSort: false,
        type: { type: 'none', options: { onClick: (cell: ICell) => () => this.deleteRow(cell) } },
        renderer: DeleteIconRenderer,
        width: '25px',
        align: 'center',
        enabled: false,
      } as any),
    );
    cols.push(
      createColumn('start', 'Start Time', {
        width: '45px',
        canSort: false,
        visible: true,
        type: { type: 'time', options: { map: (v?: any) => formatTimeNoRollover(v, this.earnedDate) } },
        map: formatTime,
        validators: startValidator,
        onValueChange: this.onGridStartChange,
      }),
    );
    cols.push(
      createColumn('end', 'End Time', {
        width: '45px',
        canSort: false,
        visible: true,
        type: { type: 'time', options: { map: (v?: any) => formatTimeNoRollover(v, this.earnedDate) } },
        map: formatTime,
        validators: endValidator,
        onValueChange: this.onGridEndChange,
      }),
    );
    cols.push(
      createColumn('units', 'Units', {
        width: '35px',
        canSort: false,
        visible: true,
        type: { type: 'number', options: { decimals: 2, thousandSeparator: false, allowNegative: false } },
        align: 'left',
        validators: unitsValidator,
        onValueChange: this.onGridUnitsChange,
      }),
    );
    cols.push(
      createColumn('reason', 'Reason', {
        width: '55px',
        canSort: false,
        visible: true,
        type: {
          type: 'auto-complete',
          options: {
            ...getAdvancedAutoCompleteOptions('Reasons', reasonAction, undefined, { openOnFocus: false }),
            ...reasonSearcher,
          },
        },
        validators: reasonValidator,
      }),
    );
    cols.push(
      createColumn('overtimeReason', 'Overtime Reason', {
        width: '75px',
        canSort: false,
        visible: false,
        type: { type: 'auto-complete', options: overtimeReasonSearcher },
      }),
    );
    cols.push(createColumn('accountCode', 'Account Code', { width: '70px', canSort: false, visible: false, type: 'text' }));
    cols.push(
      createColumn('rate', 'Rate', {
        width: '45px',
        canSort: false,
        visible: false,
        type: { type: 'number', options: { decimals: 4, thousandSeparator: false, fixed: true, allowNegative: false } },
      }),
    );
    cols.push(
      createColumn('factor', 'Factor', {
        width: '45px',
        canSort: false,
        visible: false,
        type: { type: 'number', options: { decimals: 4, thousandSeparator: false, fixed: true } },
      }),
    );
    cols.push(
      createColumn('amount', 'Amount', {
        width: '45px',
        canSort: false,
        visible: false,
        type: { type: 'number', options: { decimals: 2, thousandSeparator: true, fixed: true } },
      }),
    );

    return cols;
  }

  createGridRows(timecards?: ITimecard[]): IRowData[] {
    const rows: IRowData[] = [];

    if (timecards) {
      // load timecard data
      for (const timecard of timecards) {
        const r: IRowData = { id: timecard.id, data: { ...timecard } };
        if (timecard.effective) {
          r.data!['start'] = timecard.effective.start;
          r.data!['end'] = timecard.effective.end;
        }
        rows.push(r);
      }
    }

    return rows;
  }

  createGrid(
    timecards?: ITimecard[],
    options?: IGridOptions,
    locked?: boolean,
    onAddNewDataEntryRow?: (newDataRow: IRow) => void,
    viewOnly?: () => boolean,
  ): IGrid {
    const cols = this.createGridColumns();
    const rows = this.createGridRows(timecards);

    // create the grid model
    const gridOptions: IGridOptions = merge(
      {
        multiSelect: true,
        optionsMenuFn: () => ({
          items: createColumnsToggleMenuItems(cols, ['start', 'end', 'overtimeReason', 'accountCode', 'rate', 'factor', 'amount'], {
            onItemClick: this.toggleColumnVisibility,
          }),
        }),
        readOnly: locked || viewOnly?.(),
        rowKeybindPermissions: {
          insertRow: !locked && !viewOnly?.(),
          duplicateRow: !locked && !viewOnly?.(),
          deleteRow: !locked && !viewOnly?.(),
        },
      },
      options || {},
    );

    const grid: IGrid = createDataEntryGrid(
      rows,
      cols,
      gridOptions,
      this.completionColumnNames,
      this.defaultRowData,
      onAddNewDataEntryRow,
    );
    grid.setChangeTracking(true);

    return grid;
  }

  deleteRow(cell: ICell) {
    cell.grid.rowKeybindPermissions.deleteRow && !cell.row.data.hideDeleteIcon && cell.grid.removeRow(cell.row.id);
  }

  get gridLevelErrors(): IGridLevelErrors {
    return this.state.gridLevelErrors;
  }

  get grid(): IGrid {
    return this.state.grid;
  }

  get earnedDate(): UTC | undefined {
    return this.state.earnedDate;
  }

  get gridDataRows(): IRow[] {
    // excludes the blank data entry row in the timecardTemplate grid.
    if (isNullOrUndefined(this.grid.id)) {
      return [];
    }

    const rows = this.grid.rows.slice();

    if (rows.length) {
      rows.splice(rows.length - 1, 1);
    }

    return rows;
  }

  get gridDataEntryRow(): IRow | undefined {
    if (this.grid.rows.length <= 0) {
      return undefined;
    }

    return this.grid.rows[this.grid.rows.length - 1];
  }

  get defaultRowData(): object {
    return {
      data: {
        hideDeleteIcon: true,
        earnedDate: this.earnedDate,
      },
    };
  }

  set(data: (IRowData | undefined)[], commit = true) {
    const rowData = data.slice();
    rowData.push(this.defaultRowData);
    this.grid.set(rowData);
    commit && this.grid.commit();
  }

  get(): IRowData[] {
    // excludes the blank data entry row
    return this.gridDataRows;
  }

  addRow(newRow: IRowData, position?: number) {
    if (position === undefined) {
      position = this.grid.rows.length - 1;
    } else {
      position = Math.min(position, this.grid.rows.length - 1);
    }
    this.grid.addRow(newRow, position);
  }

  toggleColumnVisibility(item: IToggleMenuItem, column: IColumn) {
    column.visible = !column.visible;

    if (column.name === 'start') {
      const endColumn = column.grid.column('end');
      endColumn!.visible = !endColumn!.visible;
    } else if (column.name === 'end') {
      const startColumn = column.grid.column('start');
      startColumn!.visible = !startColumn!.visible;
    }
  }

  onGridStartChange(cell: ICell, value: any) {
    const timecard = cell.row.data;
    const { end, units } = cell.row.cells;
    const earnedDate = timecard.earnedDate;
    if (!(value instanceof UTC)) {
      timecard.start = !isNullOrUndefined(value) ? utc(earnedDate).startOfDay().add(value, TimeSpan.hours, 4) : undefined;
    }
    timecard.effective = timecard.start && timecard.end ? new TimestampRange(timecard.start, timecard.end) : undefined;
    if (timecard.start) {
      if (timecard.end) {
        units.value = timecard.end.subtract(timecard.start, TimeSpan.hours, 4);
      } else if (!isNullOrUndefined(timecard.units)) {
        end.value = timecard.start.add(timecard.units!, TimeSpan.hours, 4);
      }
    }
  }

  onGridEndChange(cell: ICell, value: any) {
    const timecard = cell.row.data;
    const { start, units } = cell.row.cells;
    const earnedDate = timecard.earnedDate;
    if (!(value instanceof UTC)) {
      timecard.end = !isNullOrUndefined(value) ? utc(earnedDate).startOfDay().add(value, TimeSpan.hours, 4) : undefined;
      // *** Change to make the editor know when the end time represents the next day when in 0-23 format   ***
      // *** Would still need to handle the case of the end time being the only field with a value, needing ***
      // *** the start or units change handler to potentially change the end time to the next day           ***
      // let utcEnd = !isNullOrUndefined(value) ? utc(earnedDate).startOfDay().add(value, TimeSpan.hours) : undefined;
      // if (utcEnd && ((timecard.start && timecard.start >= utcEnd) || (!timecard.start && !isNullOrUndefined(timecard.units) && timecard.units > value))) {
      //   // end is less than or equal to the start, so it must represent the next day
      //   utcEnd = utcEnd.add(1, TimeSpan.days);
      // }
      // timecard.end = utcEnd;
    }
    timecard.effective = timecard.end && timecard.start ? new TimestampRange(timecard.start, timecard.end) : undefined;
    if (timecard.end) {
      if (timecard.start) {
        units.value = timecard.end.subtract(timecard.start, TimeSpan.hours, 4);
      } else if (!isNullOrUndefined(timecard.units)) {
        start.value = timecard.end.add(-timecard.units!, TimeSpan.hours, 4);
      }
    }
  }

  onGridUnitsChange(cell: ICell, _value: any) {
    const timecard = cell.row.data;
    const { start, end } = cell.row.cells;

    if (!isNullOrUndefined(timecard.units)) {
      if (timecard.start) {
        end.value = timecard.start.add(timecard.units!, TimeSpan.hours, 4);
      } else if (timecard.end) {
        start.value = timecard.end.add(-timecard.units!, TimeSpan.hours, 4);
      }
    }
  }

  // onGridFactorChange(cell: ICell, value: any) {
  // }

  // onGridRateChange(cell: ICell, value: any) {
  // }

  // onGridAmountChange(cell: ICell, value: any) {
  // }

  recalculateHeader() {
    let totalUnits = 0;
    let minStart: UTC | undefined = undefined;
    let maxEnd: UTC | undefined = undefined;
    let reason: IReason | undefined = undefined;

    for (const row of this.grid.rows) {
      totalUnits += row.data['units'] || 0;

      const start = row.data['start'] as UTC | undefined;
      if (start && (!minStart || start < minStart)) {
        minStart = start;
      }

      const end = row.data['end'] as UTC | undefined;
      if (end && (!maxEnd || end > maxEnd)) {
        maxEnd = end;
      }

      const r = row.data['reason'];
      if (!reason || (reason.category === 'break' && r)) {
        reason = r;
      }
      if (r && r.category !== 'break' && !defaultEquals(reason, r)) {
        reason = getMultipleObject();
      }
    }

    this.form.setFieldValues({
      units: roundTo(totalUnits, 2).toString(),
      start: !isNullOrUndefined(minStart) ? formatTime(minStart) : 'N/A',
      end: !isNullOrUndefined(maxEnd) ? formatTime(maxEnd) : 'N/A',
      reason: !isNullOrUndefined(reason) ? (reason as INameEntity).name : 'N/A',
    });
  }

  hasChanges(): boolean {
    return this.grid.hasChanges;
  }

  hasErrors(): boolean {
    return (
      this.grid.hasErrors?.() ||
      Object.keys(this.gridLevelErrors).some((key: keyof IGridLevelErrors) => this.gridLevelErrors[key].length > 0)
    );
  }

  validate(): boolean {
    this.validateTimecardsExist();
    // this.validateTimecardTemplatesOverlap();
    return !this.hasErrors();
  }

  validateTimecardsExist() {
    if (this.gridDataRows.length <= 0) {
      this.state.gridLevelErrors['exist'] =
        `At least 1 detail is required. Detail required fields are: ${this.completionColumnNames.join(', ')}`;
    } else {
      this.state.gridLevelErrors['exist'] = '';
    }
  }

  showAddReasonDialog = () => {
    addReasonDialogModel.load();
  };
}

export default TimecardGridModel;
